import { useEffect } from 'react';
import MarketingLayout from '../components/marketing/MarketingLayout';
import ContentCarousel from '../components/marketing/ContentCarousel';
import RotatingHero from '../components/marketing/RotatingHero';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import CTAButton from '../components/common/CTAButton';

// Use the public path for the hero image
const heroImagePath = '/assets/Harvester1.JPG';

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle Supabase auth redirects to root with hash params
  // This catches both recovery tokens AND errors
  useEffect(() => {
    if (location.hash) {
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const tokenType = hashParams.get('type');
      const error = hashParams.get('error');
      const errorCode = hashParams.get('error_code');
      const errorDescription = hashParams.get('error_description');

      // CASE 1: Valid recovery token - redirect to reset-password page WITH the hash
      if (accessToken && tokenType === 'recovery') {
        // Redirect to /reset-password with the full hash intact
        // The ResetPasswordForm will extract and use the token
        window.location.href = `/reset-password${location.hash}`;
        return;
      }

      // CASE 2: Error in auth flow
      if (error || errorCode) {
        // Clean up the URL
        window.history.replaceState(null, '', '/');

        // Determine user-friendly message and redirect
        let message = 'An error occurred. Please try again.';
        let redirectPath = '/login';

        if (errorCode === 'otp_expired' || errorDescription?.includes('expired')) {
          message = 'Your password reset link has expired. Please request a new one.';
          redirectPath = '/forgot-password';
        } else if (errorCode === 'access_denied') {
          message = 'Access denied. Please try again or contact support.';
          redirectPath = '/login';
        }

        // Redirect with state to show error message
        navigate(redirectPath, {
          state: {
            error: message,
            fromExpiredLink: errorCode === 'otp_expired'
          },
          replace: true
        });
      }
    }
  }, [location.hash, navigate]);

  // Function to navigate to Services page and scroll to a specific section
  const navigateToServiceSection = (sectionId: string) => {
    navigate('/services', { state: { scrollToId: sectionId } });
  };

  return (
    <MarketingLayout>
      {/* Rotating Hero Section */}
      <RotatingHero />
      {/* End Rotating Hero Section */}

      {/* Investment & Insurance Split Section */}
      <section 
        className="w-full py-16 md:py-24" 
        style={{ background: "radial-gradient(circle, rgb(28, 28, 30) 0%, rgb(20, 20, 22) 100%)" }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Strategies That Work</h2>
            <p className="text-premium-light max-w-xl mx-auto mb-2 text-sm">
              Clear, practical solutions tailored for SK & AB's business owners and agricultural producers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Investment Card */}
            <div className="bg-premium-dark p-6 rounded-lg border border-gray-800/50 shadow-[0_0_40px_-10px_rgba(0,0,0,0.4)] group hover:border-amber-500/30 transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Growth-Focused Investments</h3>
              </div>
              
              <div className="mb-6">
                <div className="w-16 h-0.5 bg-amber-400 mb-4"></div>
                <p className="text-premium-light text-sm mb-4 leading-relaxed">
                  Strategic investment solutions that maximize your returns while minimizing tax impact—designed specifically for business owners and agricultural producers.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-premium-light text-sm">Tax-optimized retirement income strategies</p>
                </div>
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-premium-light text-sm">Corporate investment structures that reduce tax burden</p>
                </div>
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-premium-light text-sm">Personalized portfolios with clear performance metrics</p>
                </div>
              </div>
              
              <div className="mt-6">
                <CTAButton 
                  onClick={() => navigateToServiceSection('savings-retirement-section')} 
                  variant="primary" 
                  size="small"
                >
                  <span>See Investment Strategies</span>
                </CTAButton>
              </div>
            </div>
            
            {/* Insurance Card */}
            <div className="bg-premium-dark p-6 rounded-lg border border-gray-800/50 shadow-[0_0_40px_-10px_rgba(0,0,0,0.4)] group hover:border-brand-accent/30 transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-brand-primary/20 flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Strategic Insurance Planning</h3>
              </div>
              
              <div className="mb-6">
                <div className="w-16 h-0.5 bg-brand-accent mb-4"></div>
                <p className="text-premium-light text-sm mb-4 leading-relaxed">
                  Insurance solutions that go beyond basic coverage—strategically designed to protect your assets, reduce taxes, and create lasting wealth.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-accent mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-premium-light text-sm">Corporate-owned insurance that creates tax-free wealth</p>
                </div>
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-accent mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-premium-light text-sm">Farm succession plans with guaranteed outcomes</p>
                </div>
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-accent mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-premium-light text-sm">Buy-sell agreements funded with tax-advantaged dollars</p>
                </div>
              </div>
              
              <div className="mt-6">
                <CTAButton 
                  onClick={() => navigateToServiceSection('insurance-section')} 
                  variant="primary" 
                  size="small"
                >
                  <span>Explore Insurance Solutions</span>
                </CTAButton>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Investment & Insurance Split Section */}

      {/* Who We Serve Section */}
      <div className="w-full bg-premium-light py-16 md:py-24"> {/* Different background */}
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-premium-darker">Our Customers</h2>
          {/* Concise Intro Text */}
          <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
            Clear financial strategies designed for the unique complexities faced by agricultural producers and business owners.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            {/* Column 1: Individuals & Families */}
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
              {/* Optional: Icon */}
              <h3 className="text-xl font-semibold mb-3 text-premium-darker">Individuals & Families</h3>
              {/* Concise Individuals & Families Text */}
              <p className="text-gray-700 text-sm leading-relaxed">
                Securing your future with tailored insurance and investment strategies for retirement, education, and wealth transfer.
              </p>
            </div>

            {/* Column 2: Agriculture */}
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
              {/* Optional: Icon */}
              <h3 className="text-xl font-semibold mb-3 text-premium-darker">Agriculture Producers</h3>
              {/* Concise Agriculture Text */}
              <p className="text-gray-700 text-sm leading-relaxed">
                Supporting farm families with specialized insurance and investment strategies for succession and risk management.
              </p>
            </div>

            {/* Column 3: Businesses */}
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
              {/* Optional: Icon */}
              <h3 className="text-xl font-semibold mb-3 text-premium-darker">Business Owners</h3>
              {/* Concise Business Text */}
              <p className="text-gray-700 text-sm leading-relaxed">
                Partnering with business owners on growth and protection using effective insurance and investment structures.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* End Who We Serve Section */}

      {/* Partner logos section */}
      <div className="w-full bg-premium-darker py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold mb-8 text-white text-center">Our Trusted Partners</h2>
          <div className="w-16 h-1 bg-amber-400 mb-12 mx-auto"></div>
          <ContentCarousel />
        </div>
      </div>

      {/* Bottom CTA Section */}
      <section 
        className="w-full py-20 md:py-28 relative overflow-hidden" 
        style={{ 
          background: "linear-gradient(to right, rgb(28, 28, 30) 0%, rgb(42, 42, 46) 100%)" 
        }}
      >
        {/* Diagonal accent line */}
        <div className="absolute top-0 bottom-0 left-0 right-0 overflow-hidden z-0">
          <div className="absolute top-0 -right-1/4 bottom-0 w-1/2 bg-gradient-to-r from-transparent to-premium-dark transform rotate-6 origin-bottom-left opacity-70"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white leading-tight">
              Ready for Financial Clarity?<br />
              <span className="text-amber-400">Let's Start the Conversation</span>
            </h2>
            
            <p className="text-premium-light text-base md:text-lg mb-10 max-w-2xl mx-auto">
              Book your complimentary consultation today. We'll discuss your goals, answer your questions, and explore strategies tailored specifically for your situation.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
              <CTAButton to="/contact" variant="primary">
                <span>Book Your Consultation</span>
              </CTAButton>
              
              <CTAButton to="/services" variant="secondary">
                <span>Services</span>
              </CTAButton>
            </div>
            
            <p className="text-premium-light/70 text-sm mt-8">
              No pressure. No obligations. Just straightforward financial guidance.
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
